class AudioManager {
  private static instance: AudioManager;
  private audioContext: AudioContext;
  private sounds: { [key: string]: AudioBuffer } = {};
  private volume: number = 1.0;
  private isMuted: boolean = false;
  private gainNode: GainNode;
  private themeSource: AudioBufferSourceNode | null = null;
  private currentTheme: string | null = null;

  private constructor() {
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.gainNode = this.audioContext.createGain();
    this.gainNode.connect(this.audioContext.destination);
  }

  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  public async loadSound(name: string, url: string): Promise<void> {
    if (this.sounds[name]) {
      return;
    }

    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.sounds[name] = audioBuffer;
    } catch (error) {
      console.error(`Failed to load sound: ${name}`, error);
    }
  }

  public playSound(name: string): void {
    if (!this.sounds[name] || this.isMuted) {
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = this.sounds[name];
    source.connect(this.gainNode);
    source.start(0);
  }

  public async playTheme(name: string): Promise<void> {
    if (this.currentTheme === name) {
      return;
    }

    this.stopTheme();

    const url = `/sounds/theme-${name}.mp3`;
    await this.loadSound(name, url);

    if (!this.sounds[name] || this.isMuted) {
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = this.sounds[name];
    source.loop = true;
    source.connect(this.gainNode);
    source.start(0);

    this.themeSource = source;
    this.currentTheme = name;
  }

  public stopTheme(): void {
    if (this.themeSource) {
      this.themeSource.stop();
      this.themeSource = null;
      this.currentTheme = null;
    }
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    this.gainNode.gain.setValueAtTime(this.isMuted ? 0 : this.volume, this.audioContext.currentTime);
  }

  public getVolume(): number {
    return this.volume;
  }

  public toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.gainNode.gain.setValueAtTime(this.isMuted ? 0 : this.volume, this.audioContext.currentTime);
  }

  public getIsMuted(): boolean {
    return this.isMuted;
  }
}

export default AudioManager;
